import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ModalFooter, Button } from "@racoon/ui";
import { useT, useTypedT, Namespace } from "@racoon/i18n";
import { ConnectProto } from "@racoon/api";
import { normalizeForSearch, toTimestamp } from "@racoon/lib";
import { skipToken } from "@reduxjs/toolkit/query";
import { IcCancel, IcError } from "@racoon/icons";
import { useListTruckDriverOnShiftsQuery } from "~/shared/api/shift/driver-on-shift.grpc";
import { useListDestinationLocationsQuery } from "~/shared/api/locations/locations.grpc";
import { useListPickupLocationsQuery } from "~/shared/api/pickup-locations/pickup-locations.grpc";
import { useListBlacklistsQuery } from "~/shared/api/blacklist/blacklist.grpc";
import {
  useLazyGetDepartInfoQuery,
  useCalculateRouteWaypointsMutation,
} from "~/shared/api/cost-provider/cost-provider.api";
import { useCreatePseudoRouteMutation } from "~/shared/api/routes/route.grpc";
import { getUtcDayRange } from "~/pages/dashboard/routes/utils/dayRange";
import { useAuthSession } from "~/shared/hooks/auth.session.admin";
import {
  isJobServableByTruck,
  isSingleJobPerTripTruck,
} from "~/pages/dashboard/routes/hooks/msw-route-list/jobCategoryRouteCompatibility";
import { MapWithGeofence, MapObject } from "~/features/map-view/ui/map-view";
import { buildStepPins } from "~/features/map-view/model/helpers/step-pin-items";
import { InfoType } from "~/features/info-panel/model/useInfoPanel";
import { RouteSidebar } from "~/features/assign-route/ui/components/route-sidebar";
import { ShiftHeaderCard } from "~/features/assign-route/ui/components/shift-header-card";
import { SortableJobList } from "~/features/assign-route/ui/components/sortable-job-list";
import { JobIssue } from "~/features/assign-route/ui/components/sortable-job-row";
import { DepotBar } from "~/features/assign-route/ui/components/depot-bar";
import {
  calculateRouteMinutesWithService,
  calculateTotalRouteTimeWithService,
  filterTrucks,
  isTruckFree,
} from "~/features/assign-route/utils/route-calculations";
import { MapVisibilityStore, useMapVisible } from "~/features/assign-route/model/map-visibility";
import { useListRoutesQuery } from "~/shared/api/routes/route.grpc";
import { useJobPickerModal } from "~/shared/ui/job-picker-modal";
import { useAddPseudoJobsModal } from "../hooks/use-add-pseudo-jobs-modal";
import { useAddStopChoiceModal } from "../hooks/use-add-stop-choice-modal";
import { ShiftSlotBar } from "./components/shift-slot-bar";
import {
  PseudoStop,
  buildExistingStops,
  buildPseudoStops,
  pseudoStopKey,
} from "../model/pseudo-jobs";
import {
  MINIMUM_SLOT_MINUTES,
  computeShiftFreeSlots,
  isTimeInSlot,
  resolveTimeInSlot,
  toTimeInput,
} from "../model/shift-free-slots";
import { plannedContainerCount } from "~/shared/lib/job-containers";
import { routeOwnerOf } from "~/shared/lib/route-owner";

const JOB_CATEGORY_TO_ROUTE_CATEGORY: Partial<
  Record<ConnectProto.JobCategory, ConnectProto.RouteCategory>
> = {
  [ConnectProto.JobCategory.CO]: ConnectProto.RouteCategory.CO,
  [ConnectProto.JobCategory.MSW]: ConnectProto.RouteCategory.MSW,
  [ConnectProto.JobCategory.LIQUID]: ConnectProto.RouteCategory.LIQUID,
  [ConnectProto.JobCategory.TIPPER]: ConnectProto.RouteCategory.TIPPER,
  [ConnectProto.JobCategory.DISINFECTION]: ConnectProto.RouteCategory.DISINFECTION,
};

/** Fallback shift start when the cost provider has no departure info yet. */
const DEFAULT_START_HOUR = 8;

/** Mirrors minPseudoRouteDuration on the backend, which rejects anything shorter. */
const MINIMUM_ROUTE_MINUTES = MINIMUM_SLOT_MINUTES;

// gRPC/REST failures arrive with a machine code prefix that means nothing to an
// operator; the rest of the message is what they need to read.
const getRpcErrorMessage = (error: unknown): string | undefined => {
  const raw = error as { message?: unknown; data?: { message?: unknown } } | null;
  const message = raw?.data?.message ?? raw?.message;
  if (typeof message !== "string" || !message.trim()) return undefined;
  return message.replace(/^ERROR_CODE_[A-Z_]+:\s*/, "");
};

const startOfDay = (date: Date) => {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  return start;
};

const endOfDay = (date: Date) => {
  const end = new Date(date);
  // .999, as everywhere else in the app: a job's window has to cover the whole
  // last second of the day, not stop at the start of it.
  end.setHours(23, 59, 59, 999);
  return end;
};

interface PseudoRouteContentProps {
  // Toggled from the modal header; when off the map panel is unmounted.
  mapVisibility: MapVisibilityStore;
  onCreated?: () => void;
}

export const PseudoRouteContent: React.FC<PseudoRouteContentProps> = ({
  mapVisibility,
  onCreated,
}) => {
  const t = useTypedT(Namespace.Routes);
  const tAny = useT();
  const isMapVisible = useMapVisible(mapVisibility);
  const { activeCompanyId, user } = useAuthSession();
  const { openJobPicker } = useJobPickerModal();
  const { openAddStopChoice } = useAddStopChoiceModal();
  const { openAddPseudoJobs } = useAddPseudoJobsModal();

  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  // Stable across renders, so the date picker does not re-evaluate its bound.
  const today = useMemo(() => new Date(), []);
  const [selectedTruck, setSelectedTruck] = useState("");
  const [selectedShiftId, setSelectedShiftId] = useState("");
  // Stringified location id: see `depotsData`.
  const [selectedDepot, setSelectedDepot] = useState<string | null>(null);

  // A pseudo-route records work that has no orders behind it, so every stop is
  // a draft job — created by the backend together with the route.
  const [stops, setStops] = useState<PseudoStop[]>([]);
  // Synthetic ids must stay unique for the life of the modal: reusing the index
  // of a removed row would collide with a job still in the list.
  const draftCounter = useRef(0);

  const [isTruckPopoverOpen, setIsTruckPopoverOpen] = useState(false);
  const [truckSearchQuery, setTruckSearchQuery] = useState("");
  const [isDepotPopoverOpen, setIsDepotPopoverOpen] = useState(false);
  const [depotSearchQuery, setDepotSearchQuery] = useState("");
  const [selectedSlotIndex, setSelectedSlotIndex] = useState<number | null>(null);
  const [slotWindow, setSlotWindow] = useState({ start: "", end: "" });
  const [lastRouteCalculation, setLastRouteCalculation] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [stopLimitError, setStopLimitError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // --- Shifts / trucks -------------------------------------------------------
  const dayRange = useMemo(() => getUtcDayRange(selectedDate), [selectedDate]);

  // `currentData`, not `data`: after a date change `data` still holds the
  // previous day's shifts until the new request lands, and a route could be
  // recorded on one of them under the new day's time windows.
  const {
    currentData: truckDriverShiftsResponse,
    isFetching: isLoadingShifts,
    error: trucksError,
  } = useListTruckDriverOnShiftsQuery(
    {
      ownerOrganizationId: activeCompanyId ? BigInt(activeCompanyId) : 0n,
      datetimeFilter: {
        from: toTimestamp(new Date(dayRange.start)),
        to: toTimestamp(new Date(dayRange.end)),
      },
    },
    { skip: !activeCompanyId || !selectedDate },
  );

  const trucksWithShifts = useMemo(() => {
    return (truckDriverShiftsResponse?.truckDriverOnShifts ?? [])
      .map((item) => ({
        ...item,
        // A pseudo-route records work that already happened, so only a shift
        // that is over can hold one — the exact inverse of assign-route, which
        // drops the ended ones. The full day's set is kept for numbering, or the
        // same shift ends up "Смена 1" here and "Смена 2" in the fleet.
        allShiftsOfDay: item.driverOnShifts ?? [],
        driverOnShifts: (item.driverOnShifts ?? [])
          .filter((shift) => shift.status === ConnectProto.DriverOnShiftStatus.ENDED)
          // Tabs in shift-number order, otherwise the API order reads "Смена 2, Смена 1".
          .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)),
      }))
      .filter((item) => !!item.truck);
  }, [truckDriverShiftsResponse]);

  const mappedCurrentTruckStatuses = useMemo<ConnectProto.CurrentTruckStatus[]>(
    () =>
      trucksWithShifts.map(
        (item) =>
          ({
            truck: item.truck,
            currentShift: item.driverOnShifts?.[0],
          }) as ConnectProto.CurrentTruckStatus,
      ),
    [trucksWithShifts],
  );

  const selectedTruckData = useMemo(
    () => trucksWithShifts.find((item) => item.truck?.id.toString() === selectedTruck),
    [trucksWithShifts, selectedTruck],
  );

  // A skip loader / multilift carries one container at a time, so one job is all
  // its trip can hold. The assign-route modal enforces that by pushing every
  // extra job into a new trip; a pseudo-route records exactly one trip, so here
  // the extra stop has nowhere to go and is refused instead.
  const stopCapacity = isSingleJobPerTripTruck(selectedTruckData?.truck) ? 1 : Infinity;

  const selectedShift = useMemo(() => {
    if (!selectedTruckData?.driverOnShifts) return undefined;
    return (
      selectedTruckData.driverOnShifts.find((shift) => shift.id.toString() === selectedShiftId) ||
      selectedTruckData.driverOnShifts[0]
    );
  }, [selectedTruckData, selectedShiftId]);

  // The recorded work belongs to the shift's division, which need not be the active company.
  const routeOwnerId = routeOwnerOf(selectedShift, selectedTruckData?.truck, activeCompanyId);

  // --- Free time on the shift ------------------------------------------------
  // What the shift already has on it decides where a recorded route can go.
  const { currentData: shiftRoutesResponse, isFetching: isLoadingShiftRoutes } = useListRoutesQuery(
    selectedShift?.id && activeCompanyId
      ? {
          driverOnShiftIds: [selectedShift.id],
          ownerOrganizationIds: [BigInt(activeCompanyId)],
        }
      : skipToken,
  );

  const freeSlots = useMemo(
    () =>
      computeShiftFreeSlots({ shift: selectedShift, routes: shiftRoutesResponse?.routes ?? [] }),
    [selectedShift, shiftRoutesResponse],
  );

  const selectedSlot = selectedSlotIndex == null ? undefined : freeSlots[selectedSlotIndex];

  const selectSlot = useCallback(
    (index: number) => {
      const slot = freeSlots[index];
      if (!slot) return;
      setSelectedSlotIndex(index);
      setSlotWindow({ start: toTimeInput(slot.start), end: toTimeInput(slot.end) });
      setLastRouteCalculation("");
    },
    [freeSlots],
  );

  // A different shift is a different set of slots; the first one is offered so
  // the form is usable without a click when the shift is empty.
  useEffect(() => {
    setSelectedSlotIndex(null);
  }, [selectedShift?.id]);

  // The slot list is rebuilt whenever the shift's routes are refetched — after
  // creating a route, above all — so a held index can end up pointing at a
  // different slot, or at none. Waiting out the refetch matters: picking while
  // the stale list is still in hand would just re-apply a window that no longer
  // fits.
  useEffect(() => {
    if (isLoadingShiftRoutes) return;
    if (freeSlots.length === 0) {
      if (selectedSlotIndex != null) setSelectedSlotIndex(null);
      return;
    }
    if (selectedSlotIndex == null || !freeSlots[selectedSlotIndex]) selectSlot(0);
  }, [selectedSlotIndex, freeSlots, selectSlot, isLoadingShiftRoutes]);

  const currentTruckStatusForSelected = useMemo<ConnectProto.CurrentTruckStatus | null>(() => {
    if (!selectedTruckData?.truck) return null;
    return {
      truck: selectedTruckData.truck,
      currentShift: selectedShift,
    } as ConnectProto.CurrentTruckStatus;
  }, [selectedTruckData, selectedShift]);

  const filteredTrucks = useMemo(
    () => filterTrucks(mappedCurrentTruckStatuses, truckSearchQuery, isTruckFree),
    [mappedCurrentTruckStatuses, truckSearchQuery],
  );

  // Default truck: the first one that actually has a shift that day, since a
  // pseudo-route is always attached to one.
  useEffect(() => {
    if (trucksWithShifts.length === 0) {
      setSelectedTruck("");
      setSelectedShiftId("");
      return;
    }
    const exists = trucksWithShifts.some((item) => item.truck?.id.toString() === selectedTruck);
    if (exists) return;
    const defaultTruck =
      trucksWithShifts.find((item) => (item.driverOnShifts?.length ?? 0) > 0) ??
      trucksWithShifts[0];
    setSelectedTruck(defaultTruck.truck?.id.toString() ?? "");
  }, [trucksWithShifts, selectedTruck]);

  useEffect(() => {
    const shiftIds = (selectedTruckData?.driverOnShifts ?? []).map((shift) => shift.id.toString());
    if (shiftIds.length === 0) {
      setSelectedShiftId("");
      return;
    }
    if (!selectedShiftId || !shiftIds.includes(selectedShiftId)) {
      setSelectedShiftId(shiftIds[0]);
    }
  }, [selectedTruckData, selectedShiftId]);

  // --- Jobs currently in the route -------------------------------------------
  const stopById = useMemo(() => new Map(stops.map((stop) => [stop.tempId, stop])), [stops]);

  const jobSlots = useMemo(() => stops.map((stop) => stop.tempId), [stops]);
  const jobsList = useMemo(() => stops.map((stop) => stop.job), [stops]);

  // The duration helpers look jobs up by the ids in `jobSlots`, and an existing
  // job still carries its real one — restamped, or its service time is dropped
  // from the route's duration. The request reads `stop.job.id` instead.
  const jobsForCalculation = useMemo(
    () => stops.map((stop) => ({ ...stop.job, id: BigInt(stop.tempId) })),
    [stops],
  );

  const findJob = useCallback(
    (jobId: number | null) => (jobId === null ? undefined : stopById.get(jobId)?.job),
    [stopById],
  );

  // (container group × job type) pairs already in the route, so the picker
  // cannot offer the same stop twice.
  const existingJobKeys = useMemo(
    () => new Set(stops.flatMap((stop) => pseudoStopKey(stop) ?? [])),
    [stops],
  );

  // Jobs already in the route are hidden from the picker.
  const pickedJobIds = useMemo(
    () => stops.flatMap((stop) => (stop.kind === "existing" ? [Number(stop.job.id)] : [])),
    [stops],
  );

  // Drops the stops the newly picked truck cannot serve, instead of clearing
  // the whole list — the same rule the assign-route modal follows.
  const appliedTruckRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedTruck) {
      appliedTruckRef.current = null;
      return;
    }
    if (appliedTruckRef.current === selectedTruck) return;
    const truck = selectedTruckData?.truck;
    // Judging compatibility before the truck's own record arrives would drop
    // every stop — retry on the render that brings it.
    if (!truck) return;

    appliedTruckRef.current = selectedTruck;
    const capacity = isSingleJobPerTripTruck(truck) ? 1 : Infinity;
    setStops((prev) => {
      const kept = prev.filter((stop) => isJobServableByTruck(stop.job, truck));
      // Nothing to move the surplus into, so the leading stops are what the new
      // truck keeps — the same "keep what still fits" rule as the filter above.
      const capped = kept.length > capacity ? kept.slice(0, capacity) : kept;
      return capped.length === prev.length ? prev : capped;
    });
    setLastRouteCalculation("");
  }, [selectedTruck, selectedTruckData]);

  // The route takes only its owner's work: a shift of another division drops the existing stops
  // that division doesn't execute and hands the new ones to it, as the assign-route modal does.
  const appliedOwnerRef = useRef<string | null>(null);
  const routeOwnerKey = routeOwnerId ? String(routeOwnerId) : null;
  useEffect(() => {
    if (!selectedTruckData?.truck || !routeOwnerKey) return;
    if (appliedOwnerRef.current === routeOwnerKey) return;
    appliedOwnerRef.current = routeOwnerKey;
    const foreign = stops.filter(
      (stop) =>
        stop.kind === "existing" &&
        !!stop.job.serviceProviderId &&
        String(stop.job.serviceProviderId) !== routeOwnerKey,
    ).length;
    setStops((prev) =>
      prev.flatMap((stop): PseudoStop[] => {
        if (stop.kind === "existing") {
          const provider = stop.job.serviceProviderId;
          return provider && String(provider) !== routeOwnerKey ? [] : [stop];
        }
        return [{ ...stop, create: { ...stop.create, serviceProviderId: BigInt(routeOwnerKey) } }];
      }),
    );
    if (foreign > 0) setStopLimitError(t("jobsDroppedForRouteOwner", { count: foreign }));
    setLastRouteCalculation("");
    // Read when the owner changes, like the truck effect above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeOwnerKey, selectedTruckData]);

  const handleTruckChange = (truckId: string) => {
    setSelectedTruck(truckId);
    setSubmitError(null);
    setStopLimitError(null);
    setSuccessMessage(null);
  };

  const appendStops = (added: PseudoStop[]) => {
    if (added.length === 0) return;
    // The pickers are multi-select, so a single confirmation can overshoot the
    // capacity: take what fits, refuse the rest and say why.
    const room = stopCapacity - stops.length;
    if (room <= 0) {
      setStopLimitError(t("singleJobTruckLimit"));
      return;
    }
    const accepted = added.slice(0, room);
    setStopLimitError(accepted.length < added.length ? t("singleJobTruckLimit") : null);
    // Synthetic ids were handed out from this counter for every built stop, and
    // the accepted ones are its leading slice — advancing by their count keeps
    // the ids of later picks unique all the same.
    draftCounter.current += accepted.length;
    setStops((prev) => [...prev, ...accepted]);
    setSuccessMessage(null);
    setLastRouteCalculation("");
  };

  const addPicks = (picks: Parameters<typeof buildPseudoStops>[0]["picks"]) =>
    appendStops(
      buildPseudoStops({
        picks,
        startIndex: draftCounter.current,
        serviceProviderId: routeOwnerId,
        authorId: user?.id != null ? BigInt(user.id) : undefined,
      }),
    );

  const addExistingJobs = (jobs: ConnectProto.Job[]) =>
    appendStops(buildExistingStops(jobs, draftCounter.current));

  // The truck and the day narrow the picker.
  const openUnassignedPicker = () => {
    openJobPicker({
      serviceProviderIds: activeCompanyId ? [BigInt(activeCompanyId)] : [],
      truckId: selectedTruckData?.truck?.id,
      // Only a picked shift names the owner; until then every executor's jobs are offered.
      executorId: selectedTruckData?.truck ? routeOwnerId : undefined,
      initialDate: selectedDate,
      excludeJobIds: pickedJobIds,
      // Multi-select would only let the dispatcher pick jobs the route cannot
      // take, so a single-job truck gets a single-select picker.
      multiple: stopCapacity > 1,
      onConfirm: (_jobIds, jobs) => addExistingJobs(jobs),
    });
  };

  // Both pickers read state declared further down (locations, the truck's
  // type); they are only ever read when the dispatcher opens one.
  const openCreateJobs = () =>
    openAddPseudoJobs({
      ownerOrganizationId: activeCompanyId ? BigInt(activeCompanyId) : undefined,
      routeOwnerId,
      truckId: selectedTruckData?.truck?.id,
      truckTypeId: selectedTruckTypeId,
      locations: pickupLocations,
      existingKeys: existingJobKeys,
      // A route carries one category; until it has a stop, the picker fixes one.
      allowedJobCategory: stops.length > 0 ? stops[0].job.jobCategory : undefined,
      onAdd: addPicks,
    });

  const openStopSource = () =>
    openAddStopChoice({
      onCreate: openCreateJobs,
      onPickExisting: openUnassignedPicker,
    });

  const removeJobSlot = (slotIndex: number) => {
    setStops((prev) => prev.filter((_, index) => index !== slotIndex));
    setStopLimitError(null);
    setLastRouteCalculation("");
  };

  const moveJobSlot = (fromIndex: number, toIndex: number) => {
    setStops((prev) => {
      if (
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= prev.length ||
        toIndex >= prev.length ||
        fromIndex === toIndex
      ) {
        return prev;
      }
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
    // Order decides the calculated route, so force a recalculation.
    setLastRouteCalculation("");
    setSuccessMessage(null);
  };

  // --- Locations, depots, blacklist ------------------------------------------
  const { data: { pickupLocations = [] } = {} } = useListPickupLocationsQuery(
    { serviceProviderIds: activeCompanyId ? [BigInt(activeCompanyId)] : [] },
    { skip: !activeCompanyId },
  );

  const {
    data: locationResponse,
    isLoading: locationLoading,
    error: locationError,
  } = useListDestinationLocationsQuery(
    {
      ownerOrganizationIds: activeCompanyId ? [BigInt(activeCompanyId)] : [],
      types: [
        ConnectProto.LocationType.DISPOSAL_SITE,
        ConnectProto.LocationType.GAS_STATION,
        ConnectProto.LocationType.PROCESSING_PLANT,
        ConnectProto.LocationType.SORTING_COMPLEX,
        ConnectProto.LocationType.TRANSFER_STATION,
      ],
    },
    { skip: !activeCompanyId },
  );

  // Location ids can run past Number.MAX_SAFE_INTEGER — kept as strings and
  // turned back into bigints only at the RPC boundary, or a rounded one would
  // address a different depot than the dispatcher picked.
  const depotsData = useMemo(
    () =>
      (locationResponse?.locations ?? [])
        // Archived locations are not unload targets for a new route.
        .filter((depot) => depot.status === ConnectProto.LocationStatus.ACTIVE)
        .map((depot) => ({
          ...depot,
          id: String(depot.id),
        })),
    [locationResponse],
  );

  useEffect(() => {
    if (depotsData.length === 1 && !selectedDepot) {
      setSelectedDepot(depotsData[0].id);
    }
  }, [depotsData, selectedDepot]);

  const filteredDepots = useMemo(() => {
    if (!depotSearchQuery.trim()) return depotsData;
    const query = normalizeForSearch(depotSearchQuery);
    return depotsData.filter((depot) => normalizeForSearch(depot.name).includes(query));
  }, [depotsData, depotSearchQuery]);

  const selectedTruckTypeId = selectedTruckData?.truck?.truckType?.id;

  const candidateLocationIds = useMemo(() => {
    const ids = stops.map((stop) => String(stop.job.pickupLocationId));
    return [...new Set(ids)].sort().map(BigInt);
  }, [stops]);

  const { data: blacklistResponse } = useListBlacklistsQuery(
    {
      serviceProviderIds: activeCompanyId ? [BigInt(activeCompanyId)] : [],
      truckTypeIds: selectedTruckTypeId ? [BigInt(selectedTruckTypeId)] : [],
      pickupLocationIds: candidateLocationIds,
    },
    { skip: !activeCompanyId || !selectedTruckTypeId || candidateLocationIds.length === 0 },
  );

  const blacklistedLocationIds = useMemo(
    () =>
      new Set(
        (blacklistResponse?.blacklists ?? []).map((entry: ConnectProto.Blacklist) =>
          String(entry.pickupLocationId),
        ),
      ),
    [blacklistResponse],
  );

  // The picker only offers work the truck is equipped for, so a type mismatch
  // cannot be assembled here — the blacklist is the one rule left to check.
  const getJobIssue = useCallback(
    (jobId: number): JobIssue | null => {
      const job = findJob(jobId);
      if (!job) return null;
      return blacklistedLocationIds.has(String(job.pickupLocationId)) ? "blacklisted" : null;
    },
    [findJob, blacklistedLocationIds],
  );

  const hasBlacklistedJob = useMemo(
    () => jobSlots.some((jobId) => getJobIssue(jobId) === "blacklisted"),
    [jobSlots, getJobIssue],
  );

  // --- Route timing ----------------------------------------------------------
  const [getDepartInfo, { data: departInfo, isFetching: isDepartInfoFetching }] =
    useLazyGetDepartInfoQuery();

  useEffect(() => {
    if (selectedShift?.id) {
      getDepartInfo(Number(selectedShift.id));
    }
  }, [selectedShift?.id, getDepartInfo]);

  const [
    calculateRouteWaypoints,
    { data: routeData, isLoading: isRouteCalculating, error: routeError },
  ] = useCalculateRouteWaypointsMutation();

  const [createPseudoRoute, { isLoading: isCreating }] = useCreatePseudoRouteMutation();

  const selectedDepotData = useMemo(
    () => depotsData.find((depot) => depot.id === selectedDepot),
    [depotsData, selectedDepot],
  );

  // Where the truck set off from: the shift's departure info once the lazy query
  // lands, the depot until then. The calculation, the map and the created route
  // all read it here, so they cannot drift apart.
  const departurePoint = useMemo(() => {
    if (!selectedDepotData) return null;
    return {
      lon: departInfo?.lon ?? selectedDepotData.lon,
      lat: departInfo?.lat ?? selectedDepotData.lat,
    };
  }, [departInfo?.lon, departInfo?.lat, selectedDepotData]);

  /**
   * Identity of the calculation the form needs — everything that moves a
   * waypoint. The shift and the departure point included: switching shifts moves
   * the origin, and so does the departure query resolving. Empty while there is
   * nothing to calculate.
   */
  const routeCalculationKey = useMemo(() => {
    if (!selectedDepotData || !departurePoint || jobSlots.length === 0) return "";
    return [
      selectedTruck,
      selectedShift?.id ?? "",
      selectedDepotData.id,
      `${departurePoint.lon},${departurePoint.lat}`,
      jobSlots.join(","),
    ].join("|");
  }, [selectedDepotData, departurePoint, selectedTruck, selectedShift?.id, jobSlots]);

  const calculateRouteTime = useCallback(async () => {
    if (!selectedDepotData || !departurePoint || !routeCalculationKey) return;
    if (lastRouteCalculation === routeCalculationKey) return;

    const waypoints: { lon: number; lat: number; date_time?: Date }[] = [
      { lon: departurePoint.lon, lat: departurePoint.lat, date_time: new Date() },
    ];

    jobSlots.forEach((jobId) => {
      const pickupLocation = findJob(jobId)?.pickupLocation;
      if (pickupLocation) {
        waypoints.push({ lon: pickupLocation.lon, lat: pickupLocation.lat });
      }
    });

    waypoints.push({ lon: selectedDepotData.lon, lat: selectedDepotData.lat });

    if (waypoints.length < 2) return;
    try {
      await calculateRouteWaypoints({ waypoints }).unwrap();
      setLastRouteCalculation(routeCalculationKey);
    } catch (error) {
      console.error("Error calculating pseudo-route time:", error);
    }
  }, [
    selectedDepotData,
    departurePoint,
    routeCalculationKey,
    jobSlots,
    findJob,
    lastRouteCalculation,
    calculateRouteWaypoints,
  ]);

  useEffect(() => {
    if (routeCalculationKey) calculateRouteTime();
  }, [routeCalculationKey, calculateRouteTime]);

  /**
   * The design has no time picker: a pseudo-route starts where the shift's
   * departure info says the truck was, and lasts as long as the calculated
   * route takes — never less than the backend's minimum route duration.
   */
  const planStartAt = useMemo(() => {
    if (selectedSlot && slotWindow.start) return resolveTimeInSlot(selectedSlot, slotWindow.start);

    const fromDepart = departInfo?.date_time ? new Date(departInfo.date_time) : null;
    if (fromDepart && !isNaN(fromDepart.getTime())) return fromDepart;

    const start = new Date(selectedDate);
    start.setHours(DEFAULT_START_HOUR, 0, 0, 0);
    return start;
  }, [selectedSlot, slotWindow.start, departInfo?.date_time, selectedDate]);

  const routeMinutes = useMemo(
    () =>
      Math.max(
        calculateRouteMinutesWithService(routeData?.legs, jobSlots, jobsForCalculation),
        MINIMUM_ROUTE_MINUTES,
      ),
    [routeData, jobSlots, jobsForCalculation],
  );

  const planEndAt = useMemo(() => {
    if (selectedSlot && slotWindow.end) return resolveTimeInSlot(selectedSlot, slotWindow.end);
    return new Date(planStartAt.getTime() + routeMinutes * 60 * 1000);
  }, [selectedSlot, slotWindow.end, planStartAt, routeMinutes]);

  // The backend rejects a shorter route, and narrowing the window is manual.
  const isWindowLongEnough =
    planEndAt.getTime() - planStartAt.getTime() >= MINIMUM_ROUTE_MINUTES * 60 * 1000;

  // Bounds are kept as typed and flagged, so the operator sees what is wrong
  // instead of having it silently corrected.
  const windowInvalid = useMemo(() => {
    if (!selectedSlot) return { start: false, end: false };
    return {
      start: !isTimeInSlot(selectedSlot, slotWindow.start),
      end:
        !isTimeInSlot(selectedSlot, slotWindow.end) || planEndAt.getTime() <= planStartAt.getTime(),
    };
  }, [selectedSlot, slotWindow, planStartAt, planEndAt]);

  // --- Job row rendering -----------------------------------------------------
  const getJobDisplayName = useCallback(
    (jobId: number | null) => {
      const job = findJob(jobId);
      return job?.pickupLocation?.name || job?.pickupLocation?.address || t("noAddress");
    },
    [findJob, t],
  );

  const getJobTypeLabel = useCallback(
    (jobId: number) => {
      const job = findJob(jobId);
      return job?.type !== undefined
        ? tAny(Namespace.Orders, `types.${ConnectProto.JobType[job.type]}` as never)
        : undefined;
    },
    [findJob, tAny],
  );

  const getJobContainersInfo = useCallback(
    (jobId: number) => {
      const group = findJob(jobId)?.containerGroup;
      if (!group?.containerType?.name) return "";
      return `${group.containerType.name} (${group.numberOfContainers ?? 0})`;
    },
    [findJob],
  );

  const getJobCustomerName = useCallback(
    (jobId: number) => findJob(jobId)?.serviceReceiver?.name ?? "",
    [findJob],
  );

  const getJobVolume = useCallback(
    (jobId: number) => {
      const job = findJob(jobId);
      const group = job?.containerGroup;
      if (!job || !group) return 0;
      return (group.containerType?.volume ?? 0) * plannedContainerCount(job);
    },
    [findJob],
  );

  // Container types go down to 0.24 m³, so one decimal would round a
  // 4 × 0.24 group up to a whole cubic metre.
  const formatVolume = useCallback(
    (volume: number) => `${Math.round(volume * 100) / 100} ${t("volumeUnit")}`,
    [t],
  );

  const getJobVolumeText = useCallback(
    (jobId: number) => {
      const volume = getJobVolume(jobId);
      return volume > 0 ? formatVolume(volume) : "";
    },
    [getJobVolume, formatVolume],
  );

  // The route does not exist yet, so there are no per-stop arrival estimates to
  // compare against a schedule — the row simply omits them.
  const getJobArrivalTimeInfo = useCallback(() => null, []);

  // --- Map preview -----------------------------------------------------------
  const routeSource = useMemo(
    () =>
      departurePoint?.lat != null && departurePoint?.lon != null
        ? { lat: Number(departurePoint.lat), lon: Number(departurePoint.lon) }
        : null,
    [departurePoint],
  );

  // Numbered by the job's place in the list beside the map.
  const mapObjects = useMemo<MapObject[]>(
    () => buildStepPins(jobsList.map((job, index) => ({ job, step: String(index + 1) }))),
    [jobsList],
  );

  const mapDepots = useMemo<MapObject[]>(() => {
    if (selectedDepotData?.lat == null || selectedDepotData?.lon == null) return [];
    return [
      {
        id: String(selectedDepotData.id),
        name: selectedDepotData.name,
        type: "depot" as InfoType,
        locationType: selectedDepotData.type,
        lat: selectedDepotData.lat,
        lon: selectedDepotData.lon,
      },
    ];
  }, [selectedDepotData]);

  const mapTrucks = useMemo<MapObject[]>(() => {
    const truck = selectedTruckData?.truck;
    if (!truck || !routeSource) return [];
    return [
      {
        id: Number(truck.id),
        name: truck.plateNumber || String(truck.id),
        type: "truck" as InfoType,
        lat: routeSource.lat,
        lon: routeSource.lon,
        angle: 0,
        truckType:
          truck.type !== undefined
            ? `Truck_Type_${ConnectProto.TruckType[truck.type]}`
            : "TRUCK_TYPE_MSW",
      },
    ];
  }, [selectedTruckData, routeSource]);

  const previewRoute = useMemo(() => {
    if (!routeSource || !selectedDepotData || jobsList.length === 0) return undefined;
    return {
      // CREATED makes the builder draw the whole route as a planned (dashed) path.
      status: ConnectProto.RouteStatus.CREATED,
      jobs: jobsList,
      driverOnShift: selectedShift,
      sourceLat: routeSource.lat,
      sourceLon: routeSource.lon,
      destinationLocationId: BigInt(selectedDepotData.id),
      destinationLat: selectedDepotData.lat,
      destinationLon: selectedDepotData.lon,
    } as unknown as ConnectProto.Route;
  }, [routeSource, selectedDepotData, jobsList, selectedShift]);

  // --- Summaries -------------------------------------------------------------
  const routeDurationText = useMemo(() => {
    if (jobSlots.length === 0) return "--";
    if (isRouteCalculating) return t("calculating");
    if (!routeData?.legs) return "--";
    return calculateTotalRouteTimeWithService(
      routeData.legs,
      jobSlots,
      jobsForCalculation,
      planStartAt,
      (key) => t(key),
    );
  }, [isRouteCalculating, routeData, jobSlots, jobsForCalculation, planStartAt, t]);

  const totalVolume = useMemo(
    () => jobSlots.reduce((sum, jobId) => sum + getJobVolume(jobId), 0),
    [jobSlots, getJobVolume],
  );

  const totalDistanceKm = useMemo(() => {
    if (!routeData?.legs) return null;
    const meters = routeData.legs.reduce(
      (sum: number, leg) =>
        sum + (leg.steps ?? []).reduce((stepSum, step) => stepSum + (step.length || 0), 0),
      0,
    );
    return meters > 0 ? Math.round(meters / 1000) : null;
  }, [routeData]);

  const depotSummary = useMemo(() => {
    const parts = [`${jobSlots.length} ${t("jobsCountShort")}`];
    if (totalVolume > 0) parts.push(formatVolume(totalVolume));
    if (routeDurationText !== "--") parts.push(routeDurationText);
    return parts.join(" · ");
  }, [jobSlots.length, totalVolume, routeDurationText, formatVolume, t]);

  const totalSummary = useMemo(() => {
    const parts = [`${jobSlots.length} ${t("jobsCountShort")}`];
    if (totalVolume > 0) parts.push(formatVolume(totalVolume));
    if (totalDistanceKm != null) parts.push(`${totalDistanceKm} ${t("km")}`);
    if (routeDurationText !== "--") parts.push(routeDurationText);
    return parts.join(" · ");
  }, [jobSlots.length, totalVolume, totalDistanceKm, routeDurationText, formatVolume, t]);

  // --- Submit ----------------------------------------------------------------
  // The waypoints request is deliberately not part of this. A pseudo-route
  // records a trip that already happened: its window is the slot the operator
  // picked, so the calculation only feeds the duration and distance shown
  // alongside it — waiting on it, or refusing a route because it failed, would
  // block recording over nothing.
  const canSubmit =
    Boolean(activeCompanyId) &&
    Boolean(selectedShift?.id) &&
    selectedDepotData != null &&
    departurePoint != null &&
    stops.length > 0 &&
    stops.length <= stopCapacity &&
    isWindowLongEnough &&
    !windowInvalid.start &&
    !windowInvalid.end &&
    // The shift has to belong to the day being recorded, not the one shown before.
    !isLoadingShifts &&
    // A recorded route goes into a gap the shift actually has: while its routes
    // load the slot list says nothing, and an empty one says the shift is full.
    !isLoadingShiftRoutes &&
    selectedSlot != null &&
    !hasBlacklistedJob &&
    !trucksError &&
    !locationError;

  const handleSubmit = async () => {
    if (!canSubmit || !selectedShift?.id || !routeOwnerId) return;
    if (!selectedDepotData || !departurePoint) return;

    setSubmitError(null);
    setSuccessMessage(null);

    const routeCategory =
      JOB_CATEGORY_TO_ROUTE_CATEGORY[stops[0].job.jobCategory] ??
      ConnectProto.RouteCategory.UNDEFINED;

    const ownerOrganizationId = routeOwnerId;

    // Stop windows are the backend's to derive from the route's, so a created
    // stop carries only what the job itself is.
    const requestStops = stops.map((stop) =>
      stop.kind === "existing"
        ? { jobId: stop.job.id }
        : {
            create: {
              serviceProviderId: ownerOrganizationId,
              serviceReceiverId: stop.create.serviceReceiverId,
              driverOnShiftId: selectedShift.id,
              pickupLocationId: stop.create.pickupLocationId,
              containerGroupId: stop.create.containerGroupId,
              containerTypeId: stop.create.containerTypeId,
              type: stop.create.type,
              jobCategory: stop.create.jobCategory,
              twStartAt: toTimestamp(startOfDay(selectedDate)),
              twEndAt: toTimestamp(endOfDay(selectedDate)),
            },
          },
    );

    try {
      await createPseudoRoute({
        route: {
          ownerOrganizationId,
          driverOnShiftId: selectedShift.id,
          sourceLon: departurePoint.lon,
          sourceLat: departurePoint.lat,
          sourceLocationId: BigInt(departInfo?.id ?? selectedDepotData.id),
          destinationLon: selectedDepotData.lon,
          destinationLat: selectedDepotData.lat,
          destinationLocationId: BigInt(selectedDepotData.id),
          planStartAt: toTimestamp(planStartAt),
          planEndAt: toTimestamp(planEndAt),
          routeCategory,
        },
        stops: requestStops,
        ownerOrganizationId,
      }).unwrap();

      setSuccessMessage(t("pseudoRouteCreated"));
      setStops([]);
      setLastRouteCalculation("");
      // The route just recorded now occupies part of the shift: drop the slot
      // it went into so the refreshed free slots are offered from scratch,
      // instead of leaving its times in the fields as an invalid window.
      setSelectedSlotIndex(null);
      setSlotWindow({ start: "", end: "" });
      onCreated?.();
    } catch (error) {
      console.error("Error creating pseudo-route:", error);
      // The backend rejects what the client-side checks cannot see, so its
      // reason has to reach the operator verbatim.
      setSubmitError(getRpcErrorMessage(error) ?? t("errorCreatingPseudoRoute"));
    }
  };

  const hasLoadError = Boolean(trucksError || locationError || routeError);

  return (
    <div className="flex h-full flex-1 flex-col rounded-xl">
      <div className="flex min-h-0 flex-1 gap-4 border-t p-4">
        <RouteSidebar
          selectedDate={selectedDate}
          onDateChange={(date) => date && setSelectedDate(date)}
          // A pseudo-route records work that already happened.
          maxDate={today}
          selectedTruck={currentTruckStatusForSelected ?? undefined}
          isTruckPopoverOpen={isTruckPopoverOpen}
          onTruckPopoverChange={setIsTruckPopoverOpen}
          truckSearchQuery={truckSearchQuery}
          onTruckSearchQueryChange={setTruckSearchQuery}
          filteredTrucks={filteredTrucks}
          allTrucks={mappedCurrentTruckStatuses}
          onTruckSelect={handleTruckChange}
        />

        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <ShiftHeaderCard
            shifts={selectedTruckData?.driverOnShifts ?? []}
            allShiftsOfDay={selectedTruckData?.allShiftsOfDay ?? []}
            selectedShiftId={selectedShiftId}
            onShiftSelect={setSelectedShiftId}
          />

          {selectedShift && (
            <ShiftSlotBar
              slots={freeSlots}
              selectedSlotIndex={selectedSlotIndex}
              onSlotSelect={selectSlot}
              window={slotWindow}
              onWindowChange={setSlotWindow}
              invalid={windowInvalid}
              isLoading={isLoadingShiftRoutes}
            />
          )}

          {/* min-h-0: without it this column sizes to its content, the job list
              never gets a bounded height and its overflow-y-auto can't scroll. */}
          <div className="flex min-h-0 flex-1 flex-col">
            {/* A pseudo-route records a single trip, so the tab strip has one
                fixed entry — no add / remove / optimize menu beside it. */}
            <div className="flex items-center gap-2 rounded-t-xl border p-3">
              <div className="flex items-center rounded-xl bg-brand-blue-light p-1">
                <span className="min-w-[72px] rounded-lg bg-brand-blue px-2 py-1 text-center text-sm text-white shadow-sm">
                  {t("tripIndex", { index: 1 })}
                </span>
              </div>
            </div>

            <div className="flex min-h-0 flex-1 flex-col overflow-hidden border border-b-0 border-t-0 bg-white">
              <div className="flex flex-1 flex-col overflow-y-auto">
                <SortableJobList
                  jobSlots={jobSlots}
                  onReorder={moveJobSlot}
                  onRemoveJobSlot={removeJobSlot}
                  onOpenPicker={openStopSource}
                  getJobDisplayName={getJobDisplayName}
                  getJobTypeLabel={getJobTypeLabel}
                  getJobContainersInfo={getJobContainersInfo}
                  getJobCustomerName={getJobCustomerName}
                  getJobVolumeText={getJobVolumeText}
                  getJobArrivalTimeInfo={getJobArrivalTimeInfo}
                  trips={[]}
                  activeTripId=""
                  onMoveJobToTrip={() => {}}
                  getJobIssue={getJobIssue}
                  showMoveToTrip={false}
                  canAddJob={stops.length < stopCapacity}
                  addJobDisabledHint={t("singleJobTruckLimit")}
                />
              </div>
            </div>

            <DepotBar
              depots={depotsData}
              filteredDepots={filteredDepots}
              depotsLoading={locationLoading}
              selectedDepot={selectedDepot}
              isDepotPopoverOpen={isDepotPopoverOpen}
              onDepotPopoverChange={setIsDepotPopoverOpen}
              depotSearchQuery={depotSearchQuery}
              onDepotSearchQueryChange={setDepotSearchQuery}
              onSelectDepot={(depotId) => {
                setSelectedDepot(depotId);
                setSuccessMessage(null);
              }}
              routeDurationText={depotSummary}
            />
          </div>
        </div>

        {/* Map panel — unmounted while hidden so it stops rendering/tracking,
            and the job list takes over its width. */}
        {isMapVisible && (
          <div className="hidden min-h-0 w-[30%] shrink-0 flex-col lg:flex">
            <div className="relative min-h-0 flex-1 overflow-hidden rounded-xl border border-gray-200 bg-white">
              <MapWithGeofence
                previewRoute={previewRoute}
                objects={mapObjects}
                trucks={mapTrucks}
                depots={mapDepots}
                onMarkerClick={() => {}}
                className="z-0 h-full w-full"
              />
            </div>
          </div>
        )}
      </div>

      <ModalFooter className="shrink-0">
        <div className="flex w-full items-center justify-between gap-4">
          <div className="flex shrink-0 items-center gap-1 text-sm">
            <span className="text-text-secondary">{t("total")}:</span>
            <span className="font-medium text-text-primary">{totalSummary}</span>
          </div>

          <div className="flex min-w-0 flex-1 items-center justify-end gap-3">
            <div className="flex min-w-0 flex-wrap items-center justify-end gap-x-3 gap-y-1">
              {hasBlacklistedJob && (
                <span className="flex items-center gap-1 text-sm font-medium text-text-warning">
                  <IcError className="h-6 w-6 shrink-0" />
                  {t("truckConflictsWithLocationBlacklist")}
                </span>
              )}
              {hasLoadError && (
                <span className="flex items-center gap-1 text-sm font-medium text-brand-red">
                  <IcError className="h-6 w-6 shrink-0" />
                  {t("errorLoadingData")}
                </span>
              )}
              {stopLimitError && (
                <span className="flex items-center gap-1 text-sm font-medium text-text-warning">
                  <IcError className="h-6 w-6 shrink-0" />
                  {stopLimitError}
                  <button
                    type="button"
                    onClick={() => setStopLimitError(null)}
                    aria-label={t("close")}
                    className="shrink-0 opacity-70 transition-opacity hover:opacity-100"
                  >
                    <IcCancel className="h-5 w-5" />
                  </button>
                </span>
              )}
              {submitError && (
                <span
                  className="flex min-w-0 items-center gap-1 text-sm font-medium text-brand-red"
                  title={submitError}
                >
                  <IcError className="h-6 w-6 shrink-0" />
                  <span className="max-w-[420px] truncate">{submitError}</span>
                  <button
                    type="button"
                    onClick={() => setSubmitError(null)}
                    aria-label={t("close")}
                    className="shrink-0 opacity-70 transition-opacity hover:opacity-100"
                  >
                    <IcCancel className="h-5 w-5" />
                  </button>
                </span>
              )}
              {successMessage && (
                <span className="flex items-center gap-1 text-sm font-medium text-green-600">
                  {successMessage}
                </span>
              )}
            </div>

            <Button
              id="pseudo-route-submit-button"
              size="sm"
              variant="opacityBlueWithOutline"
              className="w-[220px] shrink-0"
              onClick={handleSubmit}
              disabled={!canSubmit || isCreating || isDepartInfoFetching}
            >
              {isCreating ? t("creating") : t("addPseudoRoute")}
            </Button>
          </div>
        </div>
      </ModalFooter>
    </div>
  );
};
